import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { forkJoin } from 'rxjs';
import _ from 'lodash';
import { MessageLevel, PgMessagesService } from 'src/app/shared/services/pg-messages.service';
import { KeycloakService } from 'src/app/authentication/keycloak.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { AuthenticationModule } from 'src/app/authentication/authentication.module';
import { MaterialOrganisationSelectorComponent } from 'src/app/shared/components/material-autocomplete/material-organisationselector.component';
import { MatSelectModule } from '@angular/material/select';
import { MaterialAutoCompleteComponent } from 'src/app/shared/components/material-autocomplete/material-auto-complete.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateTimeZoneComponentValidatorDirective, MaterialDateTimeZoneComponent, TimestampWithZone } from 'src/app/shared/components/material-date-time-zone/material-date-time-zone.component';
import { MaterialPgInputComponent } from 'src/app/shared/components/material-pg-input/material-pg-input.component';
import { MaterialPgButtonComponent } from 'src/app/shared/components/material-pg-button/material-pg-button.component';
import { Sof, Voyage } from 'src/app/generated/services/transportation/voyages/voyage';
import { MaterialPdfViewerComponent } from 'src/app/shared/components/material-pdf-viewer/material-pdf-viewer.component';
import { DocumentReference } from 'src/app/documents/documents.service';
import { MaterialChunkFileUploadComponent } from 'src/app/shared/components/material-file-uploader/material-chunk-file-upload.component';
import { ActivatedRoute, Router } from '@angular/router';
import { VoyageServiceSpa } from 'src/app/generated/services/transportation/voyages/voyageSpa';
import { LookupServiceSpa } from 'src/app/generated/services/lookups/lookupSpa';

interface SofEntryField {
  code: string;
  sofItem: string;
  timestampWithZone: TimestampWithZone;
  required: boolean;
  selected: boolean;
}

interface SofType {
  templateId: string;
  description: string;
  type: string;
}

@Component({
  selector: 'app-sof',
  templateUrl: './sof.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    MaterialDateTimeZoneComponent,
    AuthenticationModule,
    MaterialOrganisationSelectorComponent,
    MatSelectModule,
    MaterialAutoCompleteComponent,
    MatCheckboxModule,
    MaterialPgInputComponent,
    MaterialPgButtonComponent,
    DateTimeZoneComponentValidatorDirective,
    MaterialPdfViewerComponent,
    MaterialChunkFileUploadComponent,
  ]
})
export class SofComponent implements OnInit {

  @Output() changed: EventEmitter<Voyage> = new EventEmitter();
  @Output() cancelled: EventEmitter<any> = new EventEmitter();

  key = 'SOF';
  _uploadedFileId!: string;
  _newUploadedFileId!: string;
  _selectedVoyage = Voyage.create();
  edit: boolean = false;
  _selectedSofType!: SofType;
  allSofItems: string [][] = [];
  selectableSofItems: string[][] = [];
  sofEntries: SofEntryField[] = [];
  expectedVersion?: number;
  _extraSofEntry!: [string, string, string] | null;
  extraSofEntries: SofEntryField[] = [];
  showAddItem = false;
  doc?: DocumentReference;

  get selectedSofType() {
    return this._selectedSofType;
  }

  set selectedSofType(value: SofType) {
    this._selectedSofType = value;
    this.selectedSof.sofType = value.type;
    this.filterSofItemsWithType(value.type);
    this.getSofItems(value.templateId);
  }

  get extraSofEntry() {
    return this._extraSofEntry;
  }

  set extraSofEntry(value: [string, string, string] | null) {
    if (value && this.allSofItems.map(s => s[1]).includes(value[1])) {
      this.showAddItem = false;
      this._extraSofEntry = value;
      this.extraSofEntries.push({
        code: value[0],
        sofItem: value[2],
        timestampWithZone: {
          timestamp: null,
          timeZone: this.port.timezone
        },
        required: false,
        selected: true
      });

      this._extraSofEntry = null;
    }
  }

  @Input() set fileId(value: string) {
    this._uploadedFileId = value;
  }
  @Input() set updatedFileId(value: string) {
    this._newUploadedFileId = value;
  }
  @Input() set data(value: any) {
    this._selectedVoyage = value as Voyage;
  }

  selectedSof!: Sof;
  ports: Organisation[] = [];
  _port!: Organisation;

  selectedSOFTypes: SofType[] = [];

  get port() {
    return this._port;
  }

  set port(value: Organisation) {
    this._port = value;
  }
  constructor(private _organisationsService: OrganisationService,
              private _pgMessagesService: PgMessagesService,
              private _keycloakService: KeycloakService,
              private _voyagesService: VoyageServiceSpa,
              private _router: Router,
              private _activatedRoute: ActivatedRoute,
              private _lookupServiceSpa: LookupServiceSpa) { }

  ngOnInit() {
    this._activatedRoute.paramMap.subscribe(async p => {
      this._activatedRoute.queryParams.subscribe(p => {
        this._uploadedFileId = p['fileId'];
      });
      const result = await this._voyagesService.getVoyageById({
        id: p.get('id') || ''
      });

      this._selectedVoyage = result.data || Voyage.create();
      this.expectedVersion = result.version;

      if (
        this._selectedVoyage.sofs.find(
          sof => sof.fileId === this._uploadedFileId
        )
      ) {
        this.initialiseEditMode();
      } else {
        this.initialiseNewMode();
      }
    });
  }

  hasRole(role: string) {
    return this._keycloakService.hasRole(role);
  }

  saveSOF() {
    const duplicatedEntries = this.findDuplicatedEntries();
    if (duplicatedEntries.length > 0) {
      duplicatedEntries
        .map(s => `Found ${s.length} ${s[0].sofItem}s with same time`)
        .forEach(m => {
          this._pgMessagesService.publishMessage({
            message: m,
            topic: 'SOF',
            level: MessageLevel.Error
          });
        });
      return;
    }
    const duplicatedTimeEntries = this.findDuplicatedTimeEntries();
    if (duplicatedTimeEntries.length > 0) {
      duplicatedTimeEntries
        .map(s => `${s.map(s1 => s1.sofItem).join(', ')} have same times`).forEach(m => {
          this._pgMessagesService.publishMessage({
            message: m,
            topic: 'SOF',
            level: MessageLevel.Warning
          });
        });
    }
    this.selectedSof.portOrganisationId = this.port.name;
    this.selectedSof.entries = this.sofEntries.filter(s => s.selected).map(s =>
      ({
        key: s.code,
        timestamp: s.timestampWithZone.timestamp ?? 0
      }));
    this.selectedSof.entries = this.selectedSof.entries
      .concat(this.extraSofEntries.map(s =>({
        key: s.code,
        timestamp: s.timestampWithZone.timestamp ?? 0})
      ));
    if (this._newUploadedFileId) {
      this.selectedSof.fileId = this._newUploadedFileId;
    }
    this.selectedSof.portOrganisationId = this.port.name;
    if (!this._selectedVoyage.sofs.includes(this.selectedSof)) {
      if (!this._selectedVoyage.sofs) {
        this._selectedVoyage.sofs = [];
      }
      this._selectedVoyage.sofs.push(this.selectedSof);
    }
    this.updateVoyage(this._selectedVoyage);
  }

  updateVoyage(voyage: Voyage) {
    this._voyagesService.updateVoyage({data: voyage, expectedVersion: this.expectedVersion || 0}).then(() => {
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Info,
        message: `Updated voyage ${voyage.shipVoyageNo} for ${voyage.vesselName}`,
        topic: 'Voyage'
      });
      this._router.navigate(['vessels/voyage', voyage.id], {
        queryParams: {
          t: new Date().getTime()
        }
      }).then();
    });
  }

  organisationValueFormatter(data: Organisation) {
    return data.nameTranslations['en'];
  }

  removeSofItem(index: number) {
    this.extraSofEntries.splice(index, 1);
  }

  lookupItemListFormatter(item: [string, string, string]) {
    return item ? item[1] : '';
  }

  showAddSofItem() {
    this.showAddItem = true;
  }

  dateTimeZoneFocused(index: number) {
    if (index !== 0 && this.sofEntries[index].timestampWithZone.timestamp === null) {
      let search = true;
      for (let index1 = index - 1; index1 !== -1 && search ; index1 = index1 - 1) {
        if (this.sofEntries[index1].timestampWithZone !== null &&
            this.sofEntries[index1].timestampWithZone.timestamp !== null) {
          this.sofEntries[index].timestampWithZone = {
            timestamp: this.sofEntries[index1].timestampWithZone.timestamp,
            timeZone: this.sofEntries[index1].timestampWithZone.timeZone
          };
          search = false;
        }
      }
    }
  }

  skipRequiredSofEntry(l: SofEntryField) {
    if (l.selected) {
      if (confirm(`Are you sure you want to skip ${l.sofItem}`)) {
        l.selected = !l.selected;
      }
    } else {
      l.selected = !l.selected;
    }
  }

  deleteSOF() {
    if (confirm('Are you sure to delete this SOF')) {
      const i = this._selectedVoyage.sofs.findIndex(s => s.fileId === this.selectedSof.fileId);
      this._selectedVoyage.sofs.splice(i, 1);
      this.changed.emit(this._selectedVoyage);
    }
  }

  private initialiseEditMode()  {
    this.selectedSof = this._selectedVoyage.sofs.find(sof => sof.fileId === this._uploadedFileId) ?? Sof.create() ;
    this.selectedSof.entries = this.selectedSof.entries.sort((s1, s2) => s1.timestamp - s2.timestamp);
    this.edit = true;

    forkJoin([this._organisationsService.getPorts(),
      this._lookupServiceSpa.getLookupItems({categoryName: 'voyage_sof_all'})]).subscribe(results => {
      this.ports = results[0];
      this.port = this.ports.find(p => p.name === this.selectedSof.portOrganisationId) ?? Organisation.create();
      this.allSofItems = results[1].items.map(item => item.values);
      this.filterSofItemsWithType(this.selectedSof.sofType);
      this.sofEntries = this.selectedSof.entries.map(s => {
        const foundSof = this.findSofEntryByCode(s.key) ?? [] ;
        return {
          code: s.key,
          sofItem: foundSof[2],
          timestampWithZone: {
            timestamp: s.timestamp,
            timeZone: this.port.timezone
          },
          required: true,
          selected: true
        };
      });
    });
  }

  private initialiseNewMode() {
    this.selectedSof = {
      sofType: '',
      portOrganisationId: '',
      entries: [],
      fileId: this._uploadedFileId,
    };

    forkJoin([this._organisationsService.getPorts(),
      this._lookupServiceSpa.getLookupItems({categoryName:'voyage_sof_templates'}),
      this._lookupServiceSpa.getLookupItems({categoryName: 'voyage_sof_all'})])
      .subscribe(results => {
        this.ports = results[0];
        this.allSofItems = results[2].items.map(item => item.values);
        this.selectedSOFTypes = results[1].items.map(s => (
          {
            templateId: s.values[0],
            description: s.values[1],
            type: s.values[2]
          }
        ));
      });
  }

  private findSofEntryByCode(code: string) {
    const foundSof = this.allSofItems.find(s => s[0] === code);
    if (!foundSof) {
      console.error(`Could not find Sof entry for code ${code}`);
      return null;
    }
    return foundSof;
  }

  private filterSofItemsWithType(sofType: string) {
    if (sofType === 'loading') {
      this.selectableSofItems = this.allSofItems.filter(s => s[0][0] === 'L');
    } if (sofType === 'discharge') {
      this.selectableSofItems = this.allSofItems.filter(s => s[0][0] === 'D');
    }
  }

  private getSofItems(templateId: string) {
    this._lookupServiceSpa.getLookupItems({categoryName: templateId}).then(items => {
      this.sofEntries = items.items.map(i => {
        const foundSof = this.findSofEntryByCode(i.values[0]) ?? [];
        return {
          code: i.values[0],
          sofItem: foundSof[2],
          timestampWithZone: {
            timestamp: null,
            timeZone: this.port.timezone
          },
          required: (i.values[1] !== 'False' && i.values[1] !== 'false'),
          selected: (i.values[1] !== 'False' && i.values[1] !== 'false')
        };
      });
    });
  }

  private findDuplicatedEntries(): SofEntryField[][] {
    const combinedEntries = this.sofEntries.concat(this.extraSofEntries).filter(s => s.selected);
    const grouped = _.groupBy(combinedEntries, i => [i.sofItem, i.timestampWithZone.timestamp]);
    return _(grouped).filter((entries) => entries.length > 1).value();
  }

  private findDuplicatedTimeEntries(): SofEntryField[][] {
    const combinedEntries = this.sofEntries.concat(this.extraSofEntries).filter(s => s.selected);
    const grouped = _.groupBy(combinedEntries, i => i.timestampWithZone.timestamp);
    return _(grouped).filter((entries) => entries.length > 1).value();
  }

  uploadDocument(docReference: DocumentReference) {
    this.selectedSof.fileId = docReference.id;
  }

  backToVoyage(){
    this._router.navigate(['/vessels/voyage/', this._selectedVoyage?.id,]);
  }
  sofTypeFormatter(sofType: SofType) {
    return sofType ? sofType.description : '';
  }

}
